import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import MagneticButton from './MagneticButton';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    newsletter: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleCheckboxChange = (checked) => {
    setFormData((prev) => ({ ...prev, newsletter: checked }));
  };

  const validateForm = () => {
    const newErrors = {};
    const trimmedName = formData.name.trim();
    if (trimmedName.length < 2) {
      newErrors.name = 'Please enter your name';
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    const trimmedMessage = formData.message.trim();
    if (trimmedMessage.length < 10) {
      newErrors.message = 'Tell us more! At least 10 characters';
    }
    if (formData.message.length > 2500) {
      newErrors.message = 'Message too long! Max 2500 characters';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setIsSuccess(false);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      await addDoc(collection(db, 'contactus'), {
        name: formData.name.trim(),
        email: formData.email.trim(),
        content: formData.message.trim(),
        newsletter: formData.newsletter,
        timestamp: new Date().toISOString(),
      });
      setIsSuccess(true);
      setShowConfetti(true);
      setFormData({ name: '', email: '', message: '', newsletter: false });
    } catch (err) {
      setSubmitError('Oops, something broke! Try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const messageLength = formData.message.length;
  const maxLength = 2500;

  return (
    <section className="relative py-20 px-4" id="contact">
      <div className="max-w-2xl mx-auto">
        <motion.h2
          className="text-4xl md:text-5xl font-medium tracking-tight text-sp-text text-center mb-4"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          
        >
          Get in Touch
        </motion.h2>
        <motion.p
          className="text-sp-text-secondary text-center mb-12 text-lg"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Have questions or feedback? We'd love to hear from you!
        </motion.p>

        <motion.div
          className="glass-card p-8 rounded-3xl relative overflow-hidden"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Confetti particles */}
          <AnimatePresence>
            {showConfetti && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden z-50">
                {Array.from({ length: 30 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: ['var(--sp-cyan)', '#06B6D4', 'var(--sp-violet)'][i % 3],
                      top: '10%',
                      left: '50%',
                    }}
                    initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                    animate={{
                      x: (Math.random() - 0.5) * 300,
                      y: Math.random() * 300,
                      opacity: 0,
                      scale: 0,
                    }}
                    transition={{ duration: 1.5, ease: 'easeOut', delay: i * 0.03 }}
                  />
                ))}
              </div>
            )}
          </AnimatePresence>

          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              {/* Name Field */}
              <div>
                <Label htmlFor="name" className="text-white mb-2 font-medium block">
                  Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className={`bg-[#0f1214] border ${errors.name ? 'border-sp-cyan' : 'border-gray-800'} text-white placeholder:text-gray-500 focus:border-sp-cyan focus:ring-1 focus:ring-sp-cyan rounded-xl p-4 h-auto`}
                  placeholder="Your name"
                />
                {errors.name && <p className="text-sp-cyan text-sm mt-1">{errors.name}</p>}
              </div>

              {/* Email Field */}
              <div>
                <Label htmlFor="email" className="text-white mb-2 font-medium block">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`bg-[#0f1214] border ${errors.email ? 'border-sp-cyan' : 'border-gray-800'} text-white placeholder:text-gray-500 focus:border-sp-cyan focus:ring-1 focus:ring-sp-cyan rounded-xl p-4 h-auto`}
                  placeholder="your.email@example.com"
                />
                {errors.email && <p className="text-sp-cyan text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Message Field */}
              <div>
                <Label htmlFor="message" className="text-white mb-2 font-medium block">
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={maxLength}
                  rows={5}
                  className={`bg-[#0f1214] border ${errors.message ? 'border-sp-cyan' : 'border-gray-800'} text-white placeholder:text-gray-500 focus:border-sp-cyan focus:ring-1 focus:ring-sp-cyan rounded-xl resize-none p-4`}
                  placeholder="Tell us what's on your mind..."
                />
                
                {/* MOVED UP: Error message now sits immediately below the field */}
                {errors.message && <p className="text-sp-cyan text-sm mt-1">{errors.message}</p>}

                {/* Character Counter sits below the error message now */}
                <div
                  className={`text-sm mt-2 text-right ${
                    messageLength > maxLength * 0.9 ? 'text-sp-violet' : 'text-sp-text-secondary'
                  }`}
                  aria-live="polite"
                >
                  {messageLength} / {maxLength}
                </div>
              </div>

              {/* Submit Button */}
              <MagneticButton>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sp-cyan hover:bg-sp-cyan/80 text-black font-semibold py-6 text-lg rounded-xl shadow-[0_0_30px_rgba(11,217,229,0.3)] hover:shadow-[0_0_40px_rgba(11,217,229,0.5)] transition-all duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Message
                    </span>
                  )}
                </Button>
              </MagneticButton>
              {submitError && <p className="text-sp-cyan text-sm mt-2 text-center">{submitError}</p>}
            </form>
          ) : (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 10 }}
              >
                <CheckCircle className="w-20 h-20 text-sp-cyan mx-auto mb-4" />
              </motion.div>
              <h3 className="text-2xl font-medium tracking-tight text-sp-text mb-2">Message Sent!</h3>
              <p className="text-sp-text-secondary mb-6">
                We got your message. Thanks! Watch your inbox for the next drop.
              </p>
              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => setIsSuccess(false)}
                  className="border-sp-cyan text-sp-cyan hover:bg-sp-cyan hover:text-black rounded-xl"
                >
                  Send Another
                </Button>
                <a
                  href="https://play.google.com/store/apps/details?id=com.sparkthegenzapp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-sp-cyan hover:bg-sp-cyan/80 text-black rounded-xl">
                    Download Spark
                  </Button>
                </a>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;



